'use client'

import { useEffect, useRef, useCallback, useMemo } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { cn } from '@/lib/tailwind'
import type { Project } from '@/document'
import { useFormState } from './hooks/useFormState'
import { useFormAnalysis } from './hooks/useFormAnalysis'
import { useAnalysisApplication } from './hooks/useAnalysisApplication'
import { useFormSubmission } from './hooks/useFormSubmission'
import { useFormHandlers } from './hooks/useFormHandlers'
import { useStreamingVoiceRecorder } from './voice/useStreamingVoiceRecorder'
import { FormChecklistSection, FormMediaSection } from './form-sections'
import { VoiceVisualizer } from './voice/voice-visualizer'
import { AnalysisIndicator } from './analysis-indicator'
import { CaptureChipsRow } from './capture-chips-row'
import { CaptureActionBar } from './capture-action-bar'

interface PromptCaptureBoxProps {
  onSuccess?: () => void
  availableProjects?: Project[]
  enforcedProjectId?: string
  className?: string
}

export function PromptCaptureBox({
  onSuccess,
  availableProjects = [],
  enforcedProjectId,
  className,
}: PromptCaptureBoxProps) {
  const formState = useFormState({ enforcedProjectId })
  const submission = useFormSubmission()
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Voice recording refs
  const preRecordingContentRef = useRef('')
  const accumulatedTranscriptRef = useRef('')

  const handlePartialTranscript = useCallback(
    (delta: string) => {
      accumulatedTranscriptRef.current += delta
      const base = preRecordingContentRef.current
      const separator = base.trim() ? ' ' : ''
      formState.setDraftValue(
        'content',
        base + separator + accumulatedTranscriptRef.current,
      )
      if (formState.error) formState.setError(null)
    },
    [formState],
  )

  const handleFinalTranscript = useCallback(
    (transcript: string) => {
      const base = preRecordingContentRef.current
      const separator = base.trim() ? ' ' : ''
      const finalContent = base + separator + transcript
      formState.setDraftValue('content', finalContent)
      preRecordingContentRef.current = finalContent
      accumulatedTranscriptRef.current = ''
    },
    [formState],
  )

  const handleVoiceError = useCallback(
    (error: string) => {
      formState.setError(error)
    },
    [formState],
  )

  const {
    isRecording,
    isConnecting,
    startRecording: rawStartRecording,
    stopRecording,
  } = useStreamingVoiceRecorder({
    onPartialTranscript: handlePartialTranscript,
    onFinalTranscript: handleFinalTranscript,
    onError: handleVoiceError,
  })

  const startRecording = useCallback(async () => {
    preRecordingContentRef.current = formState.content
    accumulatedTranscriptRef.current = ''
    await rawStartRecording()
  }, [formState.content, rawStartRecording])

  const projectsForAnalysis = useMemo(
    () => availableProjects.map((p) => ({ id: p.id, name: p.name })),
    [availableProjects],
  )

  const fa = useFormAnalysis({
    content: formState.content,
    checklistItemsLength: formState.checklistItems?.length ?? 0,
    userSelectedType: formState.userSelectedType,
    isSubmitting: submission.isLoading,
    availableProjects: projectsForAnalysis,
  })

  useAnalysisApplication({
    formState,
    analysis: fa.analysis,
    aiDetectedType: fa.aiDetectedType,
    effectiveType: fa.effectiveType,
    isMediaDetected: fa.isMediaDetected,
    peopleForAnalysis: fa.peopleForAnalysis,
    availableTopics: fa.availableTopics,
    availableProjects: fa.availableProjects,
    enforcedProjectId,
  })

  const selectedPeople = useMemo(
    () =>
      fa.availablePeople.filter((p) =>
        formState.selectedPeopleIds.includes(p.id),
      ),
    [fa.availablePeople, formState.selectedPeopleIds],
  )

  const hasContent =
    formState.content.trim().length > 0 ||
    formState.title.trim().length > 0 ||
    Boolean(formState.checklistItems && formState.checklistItems.length > 0)

  const handlers = useFormHandlers({
    formState,
    submission,
    analysis: fa.analysis,
    aiDetectedType: fa.aiDetectedType,
    isMediaDetected: fa.isMediaDetected,
    enrichedData: fa.enrichedData,
    effectiveType: fa.effectiveType,
    selectedPeople,
    resetAnalysis: fa.resetAnalysis,
    resetEnrichment: fa.resetEnrichment,
    setEnrichedData: fa.setEnrichedData,
    onSuccess,
  })

  const showTitleAndMetadata =
    (!!fa.effectiveType ||
      (fa.isAnalyzing && formState.hasShownTitleRef.current)) &&
    !fa.isMediaDetected

  const handleTypeToggle = () => {
    const current = fa.effectiveType
    const next = current === 'task' ? 'journal' : 'task'
    formState.setUserSelectedType(next)
  }

  // Project options for chips row
  const projectOptions = useMemo(
    () => availableProjects.map((p) => ({ value: p.id, label: p.name })),
    [availableProjects],
  )

  const selectedProject = useMemo(() => {
    if (!formState.selectedProjectId) return null
    const p = availableProjects.find(
      (p) => p.id === formState.selectedProjectId,
    )
    return p ? { id: p.id, name: p.name } : null
  }, [formState.selectedProjectId, availableProjects])

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [formState.content])

  // Auto-focus on mount
  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  function handleTextareaKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (hasContent && !submission.isLoading) {
        handlers.handleSubmit(e as unknown as React.FormEvent)
      }
    } else if (e.key === 'Escape') {
      textareaRef.current?.blur()
    }
  }

  function handleSend() {
    if (hasContent && !submission.isLoading) {
      handlers.handleSubmit({ preventDefault: () => {} } as React.FormEvent)
    }
  }

  function handleAttach() {
    // Intentionally a no-op: file input in CaptureActionBar handles its own click
  }

  return (
    <div
      className={cn(
        'border-border/50 bg-card rounded-2xl border p-2 shadow-lg transition-all duration-200',
        className,
      )}
    >
      {/* Title input — visible when showTitleAndMetadata and not media */}
      <AnimatePresence>
        {showTitleAndMetadata && !fa.isMediaDetected && (
          <motion.div
            key="title-input"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <input
              type="text"
              value={formState.title}
              onChange={(e) => {
                formState.markFieldModified('title')
                formState.setDraftValue('title', e.target.value)
              }}
              placeholder="Title"
              disabled={submission.isLoading}
              className="placeholder:text-muted-foreground/50 w-full border-none bg-transparent px-1 py-1 text-lg font-medium outline-none"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content textarea */}
      <textarea
        ref={textareaRef}
        value={formState.content}
        onChange={(e) => {
          formState.setDraftValue('content', e.target.value)
          if (formState.error) formState.setError(null)
        }}
        onKeyDown={handleTextareaKeyDown}
        placeholder="What's on your mind?"
        disabled={submission.isLoading}
        rows={1}
        className="placeholder:text-muted-foreground/50 min-h-[44px] w-full resize-none border-none bg-transparent px-1 py-1 text-base outline-none"
        aria-label="Entry content"
      />

      {/* Voice visualizer */}
      <VoiceVisualizer isRecording={isRecording} />

      {/* Analysis indicator */}
      <AnalysisIndicator isAnalyzing={fa.isAnalyzing && hasContent} />

      {/* Chips row — post-analysis metadata */}
      <CaptureChipsRow
        show={showTitleAndMetadata && !fa.isMediaDetected && !fa.isAnalyzing}
        effectiveType={fa.effectiveType}
        onTypeToggle={handleTypeToggle}
        dueDate={formState.dueDate}
        onDueDateChange={(date) => {
          formState.markFieldModified('dueDate')
          formState.setDraftValue('dueDate', date)
        }}
        important={formState.important}
        onImportantChange={(val) => {
          formState.markFieldModified('important')
          formState.setDraftValue('important', val)
        }}
        selectedTopicIds={formState.selectedTopicIds}
        availableTopics={fa.availableTopics}
        onTopicsChange={handlers.handleTopicsChange}
        onAddTopic={() => {}}
        onCreateTopic={handlers.handleCreateTopic}
        selectedPeople={selectedPeople}
        availablePeople={fa.availablePeople}
        onAddPerson={handlers.handleAddPerson}
        onRemovePerson={handlers.handleRemovePerson}
        isPeopleLoading={fa.isPeopleLoading}
        onAddNewPerson={() => formState.setShowAddPerson(true)}
        suggestedPeople={formState.suggestedPeople}
        addingPerson={formState.addingPerson}
        onAddSuggestedPerson={handlers.handleAddSuggestedPerson}
        onDismissSuggestedPerson={handlers.handleDismissSuggestedPerson}
        selectedProject={selectedProject}
        projectOptions={projectOptions}
        onProjectChange={(value) => {
          formState.markFieldModified('project')
          formState.setDraftValue('selectedProjectId', value)
        }}
        projectLocked={!!enforcedProjectId}
        disabled={submission.isLoading}
        aiUpdatedFields={formState.aiUpdatedFields}
      />

      {/* Checklist section */}
      {fa.effectiveType === 'task' && !!formState.checklistItems?.length && (
        <div className="mt-1">
          <FormChecklistSection
            checklistItems={formState.checklistItems}
            onToggle={handlers.handleChecklistToggle}
            isLoading={submission.isLoading}
          />
        </div>
      )}

      {/* Media section */}
      {hasContent &&
        fa.isMediaDetected &&
        !!fa.aiDetectedType &&
        !fa.isAnalyzing && (
          <FormMediaSection
            formState={formState}
            handlers={handlers}
            hasContent={hasContent}
            isMediaDetected={fa.isMediaDetected}
            aiDetectedType={fa.aiDetectedType}
            isAnalyzing={fa.isAnalyzing}
            enrichedData={fa.enrichedData}
            isEnriching={fa.isEnriching}
            enrichmentError={fa.enrichmentError}
            retryEnrichment={fa.retryEnrichment}
            selectedPeople={selectedPeople}
            availablePeople={fa.availablePeople}
            isPeopleLoading={fa.isPeopleLoading}
            labelSuggestions={fa.labelSuggestions}
            isCheckingLabels={fa.isCheckingLabels}
            toggleLabelSuggestion={fa.toggleLabelSuggestion}
            isLoading={submission.isLoading}
          />
        )}

      {/* Error display */}
      {formState.error && (
        <p
          className="text-destructive mt-1 px-1 text-sm break-words"
          role="alert"
          aria-live="polite"
        >
          {formState.error}
        </p>
      )}

      {/* Action bar — always visible */}
      <CaptureActionBar
        hasContent={hasContent}
        isRecording={isRecording}
        isConnecting={isConnecting}
        isSubmitting={submission.isLoading}
        disabled={submission.isLoading}
        onAttach={handleAttach}
        onSend={handleSend}
        onStartRecording={startRecording}
        onStopRecording={stopRecording}
      />
    </div>
  )
}
